/**
 * `/v1/loader.js` - the republishable half of the auto-updating install.
 *
 * Served with a short cache TTL, so this is where every decision that has to
 * change after a customer has shipped lives: which recorder build to pull, the
 * canary split, and rollback. The recorder itself is content-addressed by
 * version and cached forever.
 *
 * Two ways in:
 *   - npm stub (`humanbehavior-js/auto`) already ran `init` and left config on
 *     the shared global, so start loading the recorder immediately.
 *   - a bare `<script src=".../v1/loader.js">` tag, where the page calls
 *     `HumanBehaviorTracker.init(...)` afterwards, so publish that global here.
 */

import { DEFAULT_CDN_URL, getLoaderGlobal, type LoaderGlobal } from './globals';
import { abandonQueue, createTrackerProxy } from './proxy';

declare const __HB_STABLE_VERSION__: string;
declare const __HB_CANARY_VERSION__: string;
declare const __HB_CANARY_PERCENT__: number;

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

/**
 * Where this file was served from, so the recorder is fetched from the same
 * place by default.
 *
 * Without this the script-tag install would pull the loader from wherever the
 * page's snippet points and then always look for the recorder on the default
 * CDN, so a self-hosted or staging channel would half-work in a way that is
 * hard to see: the loader loads fine, and only the recorder 404s. Must be read
 * while this script is executing, which is where module-level code runs.
 */
const servedFrom = isBrowser
    ? (() => {
          const src = (document.currentScript as HTMLScriptElement | null)?.src;
          const marker = src ? src.indexOf('/v1/loader.js') : -1;
          return marker === -1 ? null : src!.slice(0, marker);
      })()
    : null;

/**
 * Deterministic per API key, so a project stays on one side of the canary split
 * across page loads and reloads instead of flapping between two builds.
 */
function canaryBucket(apiKey: string): number {
    let hash = 2166136261;
    for (let i = 0; i < apiKey.length; i++) {
        hash ^= apiKey.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return Math.abs(hash) % 100;
}

function resolveRecorderVersion(cfg: NonNullable<LoaderGlobal['cfg']>): string {
    if (cfg.version) return cfg.version;
    if (__HB_CANARY_PERCENT__ > 0 && canaryBucket(cfg.apiKey) < __HB_CANARY_PERCENT__) {
        return __HB_CANARY_VERSION__;
    }
    return __HB_STABLE_VERSION__;
}

/**
 * The stub is frozen in the customer's bundle; this file is republished daily.
 * So this file may be years newer than the global it is handed, and it is the
 * half that has to bend. Backfill anything a newer contract added rather than
 * assuming it is there - a `TypeError` here takes out recording for every
 * customer at once, which is the one failure this whole design exists to avoid.
 */
function normalizeGlobal(g: LoaderGlobal): void {
    if (!g.settle) {
        let settle!: () => void;
        const ready = new Promise<void>((resolve) => {
            settle = resolve;
        });
        g.ready = ready;
        g.settle = settle;
    }
    if (!g.q) g.q = [];
}

function drainQueue(g: LoaderGlobal, real: Record<string, any>): void {
    // Set before settling, so anything waiting on `initializationPromise` sees
    // a live tracker rather than the proxy's pre-load behavior.
    g.real = real;
    g.settle();
    const pending = g.q.splice(0, g.q.length);
    for (const call of pending) {
        const method = real[call.m];
        if (typeof method !== 'function') {
            // The bundled stub is older or newer than the recorder it just
            // pulled. Worth surfacing: it means a real call was dropped.
            // eslint-disable-next-line no-console
            console.warn('[HumanBehavior] recorder has no method ' + call.m + '; call ignored');
            call.res(undefined);
            continue;
        }
        try {
            call.res(method.apply(real, call.a));
        } catch (error) {
            call.rej(error);
        }
    }
}

function loadRecorder(g: LoaderGlobal): void {
    const cfg = g.cfg;
    if (!cfg) return;

    const script = document.createElement('script');
    script.async = true;
    script.src =
        cfg.cdnUrl.replace(/\/$/, '') + '/v1/recorder-' + resolveRecorderVersion(cfg) + '.js';

    script.onload = () => {
        const RealTracker = (window as any).HumanBehaviorTracker;
        if (!RealTracker || typeof RealTracker.init !== 'function') {
            abandonQueue(g);
            return;
        }
        try {
            drainQueue(g, RealTracker.init(cfg.apiKey, cfg.options));
        } catch {
            abandonQueue(g);
        }
    };
    script.onerror = () => abandonQueue(g);

    (document.head || document.documentElement).appendChild(script);
}

if (isBrowser) {
    const g = getLoaderGlobal();
    normalizeGlobal(g);

    if (g.cfg) {
        // npm stub path: config is already there, the queue may already have
        // calls in it.
        loadRecorder(g);
    } else {
        // Script-tag path: the page has not called init yet. Stand in for the
        // real class until the recorder overwrites this global with itself.
        const shim = {
            init(apiKey: string, options: Record<string, any> = {}) {
                const { cdnUrl, version, ...trackerOptions } = options;
                if (!g.cfg) {
                    g.cfg = {
                        apiKey,
                        options: trackerOptions,
                        cdnUrl: cdnUrl || servedFrom || DEFAULT_CDN_URL,
                        version,
                    };
                    loadRecorder(g);
                }
                return createTrackerProxy(g);
            },
        };
        (window as any).HumanBehaviorTracker = shim;
        (window as any).humanbehavior = { init: shim.init };
    }
}
